import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { LoginSEO, RegisterSEO } from '../components/SEO';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, Building } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (token && user.role === 'customer') {
      handleSuccessfulAuth();
    }
  }, []);

  const handleSuccessfulAuth = () => {
    // Check if there's checkout data to return to
    const checkoutReturn = sessionStorage.getItem('checkoutReturn');
    if (checkoutReturn) {
      const data = JSON.parse(checkoutReturn);
      sessionStorage.removeItem('checkoutReturn');
      navigate('/checkout', { state: data });
    } else if (location.state?.returnTo) {
      navigate(location.state.returnTo, { state: location.state.checkoutData });
    } else {
      navigate('/my-orders');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/login`, loginData);
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success(`Welcome back, ${user.name}!`);
      handleSuccessfulAuth();
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.name || !registerData.email || !registerData.phone || !registerData.password) {
      toast.error('Please fill all required fields');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/register`, {
        name: registerData.name,
        email: registerData.email,
        phone: registerData.phone,
        company: registerData.company || undefined,
        password: registerData.password
      });
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('Account created successfully!');
      handleSuccessfulAuth();
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {isLogin ? <LoginSEO /> : <RegisterSEO />}
      <Header />
      
      <div className="max-w-md mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl font-bold mb-2 text-[#1a1a1a]">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="font-manrope text-sm text-[#737373]">
            {isLogin 
              ? 'Login to continue with your order' 
              : 'Register to start ordering media coverage'
            }
          </p>
        </div>

        {/* Toggle Tabs */}
        <div className="flex border border-[#e5e5e5] mb-8">
          <button
            onClick={() => setIsLogin(true)}
            data-testid="login-tab"
            className={`flex-1 px-4 py-3 font-manrope text-xs uppercase tracking-widest transition-all ${
              isLogin ? 'bg-[#D4AF37] text-[#1a1a1a]' : 'bg-[#fafafa] text-[#737373] hover:bg-[#f5f5f5]'
            }`}
          >
            Login
          </button>
          <button
            onClick={() => setIsLogin(false)}
            data-testid="register-tab"
            className={`flex-1 px-4 py-3 font-manrope text-xs uppercase tracking-widest transition-all ${
              !isLogin ? 'bg-[#D4AF37] text-[#1a1a1a]' : 'bg-[#fafafa] text-[#737373] hover:bg-[#f5f5f5]'
            }`}
          >
            Register
          </button>
        </div>

        {/* Login Form */}
        {isLogin ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                <input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                  data-testid="login-email-input"
                  placeholder="your@email.com"
                  className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none placeholder-[#a3a3a3]"
                />
              </div>
            </div>
            
            <div>
              <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none placeholder-[#a3a3a3]"
                />
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full px-8 py-4 bg-[#D4AF37] text-[#1a1a1a] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        ) : (
          /* Register Form */
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Full Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                <input
                  type="text"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                  data-testid="register-name-input"
                  placeholder="John Doe"
                  className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none placeholder-[#a3a3a3]"
                />
              </div>
            </div>
            
            <div>
              <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  data-testid="register-email-input"
                  placeholder="your@email.com"
                  className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none placeholder-[#a3a3a3]"
                />
              </div>
            </div>
            
            <div>
              <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Phone *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                <input
                  type="tel"
                  value={registerData.phone}
                  onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                  data-testid="register-phone-input"
                  placeholder="+91 98765 43210"
                  className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none placeholder-[#a3a3a3]"
                />
              </div>
            </div>
            
            <div>
              <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Company (Optional)</label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                <input
                  type="text"
                  value={registerData.company}
                  onChange={(e) => setRegisterData({...registerData, company: e.target.value})}
                  data-testid="register-company-input"
                  placeholder="Your Company"
                  className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none placeholder-[#a3a3a3]"
                />
              </div>
            </div>
            
            <div>
              <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                <input
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                  data-testid="register-password-input"
                  placeholder="Min 6 characters"
                  className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none placeholder-[#a3a3a3]"
                />
              </div>
            </div>
            
            <div>
              <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Confirm Password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                <input
                  type="password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                  data-testid="register-confirm-password-input"
                  placeholder="Repeat password"
                  className="w-full border border-[#e5e5e5] bg-[#fafafa] text-[#1a1a1a] pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none placeholder-[#a3a3a3]"
                />
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full px-8 py-4 bg-[#D4AF37] text-[#1a1a1a] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
        )}

        <p className="font-manrope text-xs text-[#a3a3a3] text-center mt-6">
          By continuing, you agree to our{' '}
          <a href="/terms" className="underline text-[#D4AF37]">Terms of Service</a> and{' '}
          <a href="/privacy" className="underline text-[#D4AF37]">Privacy Policy</a>
        </p>
      </div>
      
      <Footer />
    </div>
  );
};

export default CustomerAuth;
